import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  PanelBottom,
  Plus,
  Trash2,
  LayoutTemplate,
  Link2,
  Phone,
  Image as ImageIcon,
  Blocks,
  Paintbrush,
  FilePlus,
  RotateCcw,
  X,
  Sparkles,
} from 'lucide-react';
import { pageBuilderApi } from '../../lib/api';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import type { KreatorSettingsData } from '../KreatorPage';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
  /** Override dla globalnego footera w panelu super-admina (wgrany JSON) - banner zamiast przycisków buildera. */
  systemFooterUploader?: React.ReactNode;
}

type FooterTemplateId = 'custom' | 'style-1' | 'style-2';

interface FooterTemplate {
  id: FooterTemplateId;
  name: string;
  description: string;
}

const FOOTER_TEMPLATES: FooterTemplate[] = [
  {
    id: 'custom',
    name: 'Spersonalizowana',
    description: 'Dedykowany projekt w EcomFlow Visual Builder',
  },
  {
    id: 'style-1',
    name: 'Klasyczny',
    description: 'Logo + kolumny z linkami + copyright',
  },
  {
    id: 'style-2',
    name: 'Minimalny',
    description: 'Linki + copyrights',
  },
];

const MAX_CLASSIC_COLUMNS = 4;

type FooterCustomMode = 'default' | 'blank';

type FooterForm = {
  templateId: FooterTemplateId;
  customMode: FooterCustomMode;
  columns: Array<{ title: string; links: Array<{ label: string; url: string }> }>;
  copyrightText: string;
  socialLinks: Array<{ platform: string; url: string }>;
  logoUrl: string | null;
  useCustomColors: boolean;
  fontColor: string | null;
  headingColor: string | null;
  backgroundColor: string | null;
  paddingTop: number | '';
  paddingBottom: number | '';
};

function normalizeFooterPadding(value: number | ''): number {
  if (value === '') return 0;
  return Math.max(0, Math.min(200, Number(value) || 0));
}

export function KreatorFooterSection({ integrationId, settings, onSaved, showSuccess, showError, apiAdapter, systemFooterUploader }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [openingBuilder, setOpeningBuilder] = useState<FooterCustomMode | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [resettingFooter, setResettingFooter] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const defaultFontColor = settings?.settings?.fontColor || '#1a1a1a';
  // Fall back to the H4 scheme color — classic footer column titles use H4.
  const defaultHeadingColor = settings?.settings?.fontColor || '#1a1a1a';
  const defaultBackgroundColor = settings?.settings?.backgroundColor || '#ffffff';

  const [form, setForm] = useState<FooterForm>({
    templateId: (settings?.footer.templateId as FooterTemplateId) || 'custom',
    customMode: (settings?.footer.customMode as FooterCustomMode) || 'default',
    columns: settings?.footer.columns || [],
    copyrightText: settings?.footer.copyrightText || '',
    socialLinks: settings?.footer.socialLinks || [],
    logoUrl: settings?.footer.logoUrl ?? null,
    useCustomColors: settings?.footer.useCustomColors ?? false,
    fontColor: settings?.footer.fontColor ?? null,
    headingColor: settings?.footer.headingColor ?? null,
    backgroundColor: settings?.footer.backgroundColor ?? null,
    paddingTop: settings?.footer.paddingTop ?? 60,
    paddingBottom: settings?.footer.paddingBottom ?? 60,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await adapter.updateFooter({
        ...form,
        paddingTop: normalizeFooterPadding(form.paddingTop),
        paddingBottom: normalizeFooterPadding(form.paddingBottom),
        backgroundColor: form.backgroundColor ? normalizeHexColor(form.backgroundColor) : null,
        fontColor: form.fontColor ? normalizeHexColor(form.fontColor) : null,
        headingColor: form.headingColor ? normalizeHexColor(form.headingColor) : null,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenBuilder = useCallback(async (mode: FooterCustomMode) => {
    setOpeningBuilder(mode);
    try {
      const res = await pageBuilderApi.getOrCreateFooterPage(integrationId, mode);
      navigate(`/kreator/${integrationId}/builder/${res.data.id}`, {
        state: { returnUrl: `/kreator/${integrationId}/footer` },
      });
    } catch (err) {
      showError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się otworzyć buildera');
    } finally {
      setOpeningBuilder(null);
    }
  }, [integrationId, navigate, showError]);

  const handleResetFooter = useCallback(async () => {
    setResettingFooter(true);
    try {
      await pageBuilderApi.resetFooterPage(integrationId);
      showSuccess('Przywrócono domyślny szablon EcomFlow Footer.');
      setConfirmReset(false);
    } catch (err) {
      showError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się przywrócić domyślnego szablonu');
    } finally {
      setResettingFooter(false);
    }
  }, [integrationId, showSuccess, showError]);

  const addColumn = () => {
    if (form.columns.length >= MAX_CLASSIC_COLUMNS) return;
    setForm({
      ...form,
      columns: [...form.columns, { title: '', links: [] }],
    });
  };

  const removeColumn = (index: number) => {
    setForm({
      ...form,
      columns: form.columns.filter((_, i) => i !== index),
    });
  };

  const updateColumn = (index: number, field: string, value: unknown) => {
    const columns = [...form.columns];
    columns[index] = { ...columns[index], [field]: value };
    setForm({ ...form, columns });
  };

  const addLink = (colIndex: number) => {
    const columns = [...form.columns];
    columns[colIndex] = {
      ...columns[colIndex],
      links: [...columns[colIndex].links, { label: '', url: '' }],
    };
    setForm({ ...form, columns });
  };

  const removeLink = (colIndex: number, linkIndex: number) => {
    const columns = [...form.columns];
    columns[colIndex] = {
      ...columns[colIndex],
      links: columns[colIndex].links.filter((_, i) => i !== linkIndex),
    };
    setForm({ ...form, columns });
  };

  const updateLink = (colIndex: number, linkIndex: number, field: string, value: string) => {
    const columns = [...form.columns];
    const links = [...columns[colIndex].links];
    links[linkIndex] = { ...links[linkIndex], [field]: value };
    columns[colIndex] = { ...columns[colIndex], links };
    setForm({ ...form, columns });
  };

  const fontColorValue = form.fontColor ?? defaultFontColor;
  const headingColorValue = form.headingColor ?? defaultHeadingColor;
  const backgroundColorValue = form.backgroundColor ?? defaultBackgroundColor;

  const renderAppearanceSettings = () => (
    <div className="card">
      <SectionHeader
        icon={Paintbrush}
        title="Wygląd i odstępy stopki"
        description="Domyślnie stopka dziedziczy kolory z ustawień szablonu. Możesz je nadpisać tutaj oraz ustawić własne odstępy."
        accent="tertiary"
      />

      {/* Paddings — zawsze aktywne */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Odstęp od góry (px)
          </label>
          <input
            type="number"
            min={0}
            max={200}
            value={form.paddingTop}
            onChange={(e) => setForm({ ...form, paddingTop: e.target.value === '' ? '' : Number(e.target.value) })}
            onBlur={() => setForm(prev => ({ ...prev, paddingTop: normalizeFooterPadding(prev.paddingTop) }))}
            className="input w-full text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Odstęp od dołu (px)
          </label>
          <input
            type="number"
            min={0}
            max={200}
            value={form.paddingBottom}
            onChange={(e) => setForm({ ...form, paddingBottom: e.target.value === '' ? '' : Number(e.target.value) })}
            onBlur={() => setForm(prev => ({ ...prev, paddingBottom: normalizeFooterPadding(prev.paddingBottom) }))}
            className="input w-full text-sm"
          />
        </div>
      </div>

      {/* Custom colors toggle */}
      <div className="border-t border-border-subtle pt-4">
        <label className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={form.useCustomColors}
            onChange={(e) => setForm({ ...form, useCustomColors: e.target.checked })}
            className="w-4 h-4 rounded border-border-strong bg-surface-raised"
          />
          <span className="text-sm text-text-secondary">Użyj własnych kolorów stopki</span>
        </label>

        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${form.useCustomColors ? '' : 'opacity-40 pointer-events-none'}`}>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Kolor tła</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={safeColorForPicker(backgroundColorValue, defaultBackgroundColor)}
                onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })}
                className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer shrink-0"
              />
              <input
                type="text"
                value={backgroundColorValue}
                onChange={(e) => setForm({ ...form, backgroundColor: prefixHexColor(e.target.value) })}
                onBlur={(e) => setForm({ ...form, backgroundColor: normalizeHexColor(e.target.value) })}
                className="input flex-1 text-sm font-mono"
              />
            </div>
            <span className="text-[11px] text-text-muted mt-1 block">
              Domyślnie: {defaultBackgroundColor}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Kolor tekstu</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={safeColorForPicker(fontColorValue, defaultFontColor)}
                onChange={(e) => setForm({ ...form, fontColor: e.target.value })}
                className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer shrink-0"
              />
              <input
                type="text"
                value={fontColorValue}
                onChange={(e) => setForm({ ...form, fontColor: prefixHexColor(e.target.value) })}
                onBlur={(e) => setForm({ ...form, fontColor: normalizeHexColor(e.target.value) })}
                className="input flex-1 text-sm font-mono"
              />
            </div>
            <span className="text-[11px] text-text-muted mt-1 block">
              Domyślnie: {defaultFontColor}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Kolor nagłówków</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={safeColorForPicker(headingColorValue, defaultHeadingColor)}
                onChange={(e) => setForm({ ...form, headingColor: e.target.value })}
                className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer shrink-0"
              />
              <input
                type="text"
                value={headingColorValue}
                onChange={(e) => setForm({ ...form, headingColor: prefixHexColor(e.target.value) })}
                onBlur={(e) => setForm({ ...form, headingColor: normalizeHexColor(e.target.value) })}
                className="input flex-1 text-sm font-mono"
              />
            </div>
            <span className="text-[11px] text-text-muted mt-1 block">
              Domyślnie: {defaultHeadingColor}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <PanelBottom className="w-6 h-6 text-accent-primary" />
          Stopka
        </h1>
        <p className="text-text-secondary mt-1">Wygląd i treść stopki sklepu</p>
      </div>

      {/* Template Selection */}
      <div className="card">
        <SectionHeader
          icon={LayoutTemplate}
          title="Szablon stopki"
          description="Wybierz układ i styl stopki sklepu"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {FOOTER_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => setForm({ ...form, templateId: tpl.id })}
              className={`p-4 rounded-lg border text-left transition-all ${
                form.templateId === tpl.id
                  ? 'border-accent-primary/50 bg-accent-primary/10'
                  : 'border-border bg-surface-raised hover:border-border-strong'
              }`}
            >
              <div className="text-sm font-medium text-text-primary">{tpl.name}</div>
              <div className="text-xs text-text-secondary mt-1">{tpl.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── CUSTOM (super-admin tryb): upload JSON eksportu z buildera ─ */}
      {form.templateId === 'custom' && !adapter.isTenantScoped && (
        <div className="card">
          <SectionHeader
            icon={Sparkles}
            title="Globalna stopka spersonalizowana"
            description="Wgraj JSON eksportu z EcomFlow Buildera. Po reset-do-domyślnych w panelu sklepu, zawartość zostanie skopiowana do tenant'a."
            accent="secondary"
          />
          {systemFooterUploader}
        </div>
      )}

      {/* ── CUSTOM (tryb sklepu): mode tabs + builder ───────────────── */}
      {form.templateId === 'custom' && adapter.isTenantScoped && (
        <div className="card">
          <SectionHeader
            icon={Sparkles}
            title="Wariant spersonalizowanej stopki"
            description="Wybierz, który projekt ma się pokazywać na sklepie. Oba warianty są przechowywane niezależnie - możesz dowolnie między nimi przełączać bez utraty pracy."
            accent="secondary"
          />

          {/* Tabs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => setForm({ ...form, customMode: 'default' })}
              className={`p-4 rounded-lg border text-left transition-all ${
                form.customMode === 'default'
                  ? 'border-accent-primary/50 bg-accent-primary/10'
                  : 'border-border bg-surface-raised hover:border-border-strong'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-accent-primary/10 shrink-0">
                  <LayoutTemplate className="w-4 h-4 text-accent-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">EcomFlow Footer</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    Domyślny, starannie zaprojektowany szablon stopki EcomFlow Footer
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm({ ...form, customMode: 'blank' })}
              className={`p-4 rounded-lg border text-left transition-all ${
                form.customMode === 'blank'
                  ? 'border-accent-tertiary/50 bg-accent-tertiary/10'
                  : 'border-border bg-surface-raised hover:border-border-strong'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-accent-tertiary/10 shrink-0">
                  <FilePlus className="w-4 h-4 text-accent-tertiary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">Własny projekt</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    Pusty canvas - zaprojektuj stopkę od zera, bez żadnych wstępnych elementów.
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Builder opener + contextual actions for the active mode */}
          <div className="border-t border-border-subtle pt-5">
            <div
              onClick={() => { if (openingBuilder === null) handleOpenBuilder(form.customMode); }}
              className={`flex items-center gap-4 p-5 rounded-xl border-2 border-dashed transition-all ${
                form.customMode === 'default'
                  ? 'border-accent-primary/20 hover:border-accent-primary/40 hover:bg-accent-primary/5'
                  : 'border-accent-tertiary/20 hover:border-accent-tertiary/40 hover:bg-accent-tertiary/5'
              } ${openingBuilder !== null ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                form.customMode === 'default' ? 'bg-accent-primary/10' : 'bg-accent-tertiary/10'
              }`}>
                {openingBuilder === form.customMode ? (
                  <Loader2 className={`w-5 h-5 animate-spin ${form.customMode === 'default' ? 'text-accent-primary' : 'text-accent-tertiary'}`} />
                ) : (
                  <Blocks className={`w-5 h-5 ${form.customMode === 'default' ? 'text-accent-primary' : 'text-accent-tertiary'}`} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-text-primary">
                  {form.customMode === 'default'
                    ? 'Otwórz edytor - EcomFlow Footer'
                    : 'Otwórz edytor - Własny projekt'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {form.customMode === 'default'
                    ? 'Edytuj gotowy szablon. Zmiany zapisujesz w edytorze - nie stracisz ich, nawet jeśli przełączysz się na własny projekt.'
                    : 'Zaprojektuj stopkę od podstaw na pustym canvasie. Projekt jest przechowywany osobno od domyślnego szablonu.'}
                </p>
              </div>
            </div>

            {/* Reset button — visible only in "EcomFlow Footer" mode */}
            {form.customMode === 'default' && (
              <div className="mt-4 flex items-start gap-3 p-4 rounded-lg bg-surface-base/40 border border-border-subtle">
                <div className="p-2 rounded-lg bg-amber-400/10 shrink-0">
                  <RotateCcw className="w-4 h-4 text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text-primary">Przywróć domyślne</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    Jeśli coś pójdzie nie tak, możesz wrócić do pierwotnej wersji szablonu EcomFlow Footer. Reset nie ma wpływu na projekt z zakładki "Własny projekt"
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmReset(true)}
                  disabled={resettingFooter}
                  className="btn-secondary text-xs flex items-center gap-1.5 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {resettingFooter ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Resetuj
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CLASSIC (style-1): logo + columns + contact info ─────────── */}
      {form.templateId === 'style-1' && (
        <>
          {/* Logo */}
          <div className="card">
            <SectionHeader
              icon={ImageIcon}
              title="Logo stopki"
              description="Logo wyświetlane nad kolumnami z linkami (opcjonalne)"
            />
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Grafika logo</label>
              {form.logoUrl ? (
                <div className="relative inline-block p-4 bg-surface-base rounded-lg border border-border">
                  <img src={form.logoUrl} alt="Logo stopki" className="max-h-16 max-w-[220px] object-contain" />
                  <button
                    onClick={() => setForm({ ...form, logoUrl: null })}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-danger rounded-full flex items-center justify-center text-text-primary hover:bg-danger"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowMediaPicker(true)}
                  className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-accent-primary/30 transition-all"
                >
                  <ImageIcon className="w-4 h-4" />
                  Wybierz z biblioteki
                </button>
              )}
            </div>
          </div>

          {/* Columns */}
          <div className="card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <SectionHeader
                icon={Link2}
                title="Kolumny z linkami"
                description={`Maksymalnie ${MAX_CLASSIC_COLUMNS} kolumny. Każda może mieć do 20 linków.`}
                accent="secondary"
                className="!mb-0 flex-1"
              />
              <button
                onClick={addColumn}
                disabled={form.columns.length >= MAX_CLASSIC_COLUMNS}
                className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Plus className="w-4 h-4" /> Dodaj kolumnę
              </button>
            </div>

            {form.columns.length === 0 ? (
              <p className="text-sm text-text-muted">Brak kolumn. Kliknij "Dodaj kolumnę" aby rozpocząć.</p>
            ) : (
              <div className="space-y-4">
                {form.columns.map((col, ci) => (
                  <div key={ci} className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="text"
                        value={col.title}
                        onChange={(e) => updateColumn(ci, 'title', e.target.value)}
                        placeholder="Nazwa kolumny"
                        className="input flex-1"
                      />
                      <button
                        onClick={() => removeColumn(ci)}
                        className="btn-icon text-danger hover:text-danger"
                        title="Usuń kolumnę"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {col.links.map((link, li) => (
                      <div key={li} className="flex items-center gap-2 mb-2 ml-4">
                        <input
                          type="text"
                          value={link.label}
                          onChange={(e) => updateLink(ci, li, 'label', e.target.value)}
                          placeholder="Tekst"
                          className="input flex-1 text-sm"
                        />
                        <input
                          type="text"
                          value={link.url}
                          onChange={(e) => updateLink(ci, li, 'url', e.target.value)}
                          placeholder="URL"
                          className="input flex-1 text-sm"
                        />
                        <button
                          onClick={() => removeLink(ci, li)}
                          className="text-danger hover:text-danger"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={() => addLink(ci)}
                      className="text-xs text-accent-primary hover:text-accent-primary/80 ml-4 mt-1"
                    >
                      + Dodaj link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {renderAppearanceSettings()}

          {/* Copyright + social */}
          <div className="card">
            <SectionHeader
              icon={Phone}
              title="Dane kontaktowe"
              description="Copyright i linki społecznościowe"
              accent="success"
            />
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Tekst copyright</label>
                <input
                  type="text"
                  value={form.copyrightText}
                  onChange={(e) => setForm({ ...form, copyrightText: e.target.value })}
                  placeholder="© 2026 Twoja Firma. Wszelkie prawa zastrzeżone."
                  className="input w-full"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-text-secondary">Social media</label>
                  <button
                    onClick={() =>
                      setForm({
                        ...form,
                        socialLinks: [...form.socialLinks, { platform: 'facebook', url: '' }],
                      })
                    }
                    className="text-xs text-accent-primary hover:text-accent-primary/80"
                  >
                    + Dodaj
                  </button>
                </div>
                {form.socialLinks.map((sl, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <select
                      value={sl.platform}
                      onChange={(e) => {
                        const links = [...form.socialLinks];
                        links[i] = { ...links[i], platform: e.target.value };
                        setForm({ ...form, socialLinks: links });
                      }}
                      className="input w-36 text-sm"
                    >
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="twitter">Twitter / X</option>
                      <option value="youtube">YouTube</option>
                      <option value="tiktok">TikTok</option>
                      <option value="linkedin">LinkedIn</option>
                    </select>
                    <input
                      type="text"
                      value={sl.url}
                      onChange={(e) => {
                        const links = [...form.socialLinks];
                        links[i] = { ...links[i], url: e.target.value };
                        setForm({ ...form, socialLinks: links });
                      }}
                      placeholder="https://..."
                      className="input flex-1 text-sm"
                    />
                    <button
                      onClick={() =>
                        setForm({
                          ...form,
                          socialLinks: form.socialLinks.filter((_, idx) => idx !== i),
                        })
                      }
                      className="text-danger hover:text-danger"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── MINIMAL (style-2): links + copyright ─────────────────────── */}
      {form.templateId === 'style-2' && (
        <>
          <div className="card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <SectionHeader
                icon={Link2}
                title="Linki"
                description="Wyświetlane po lewej stronie stopki, obok copyright"
                accent="secondary"
                className="!mb-0 flex-1"
              />
              <button
                onClick={() => {
                  // Minimal template uses a single "column" to store the flat list of links
                  const first = form.columns[0] ?? { title: '', links: [] };
                  const updated = { ...first, links: [...first.links, { label: '', url: '' }] };
                  setForm({ ...form, columns: [updated] });
                }}
                className="btn-secondary text-sm flex items-center gap-1 shrink-0"
              >
                <Plus className="w-4 h-4" /> Dodaj link
              </button>
            </div>

            {(() => {
              const links = form.columns[0]?.links ?? [];
              if (links.length === 0) {
                return <p className="text-sm text-text-muted">Brak linków. Kliknij "Dodaj link" aby rozpocząć.</p>;
              }
              return (
                <div className="space-y-2">
                  {links.map((link, li) => (
                    <div key={li} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => {
                          const first = form.columns[0] ?? { title: '', links: [] };
                          const newLinks = [...first.links];
                          newLinks[li] = { ...newLinks[li], label: e.target.value };
                          setForm({ ...form, columns: [{ ...first, links: newLinks }] });
                        }}
                        placeholder="Tekst (np. Regulamin)"
                        className="input flex-1 text-sm"
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => {
                          const first = form.columns[0] ?? { title: '', links: [] };
                          const newLinks = [...first.links];
                          newLinks[li] = { ...newLinks[li], url: e.target.value };
                          setForm({ ...form, columns: [{ ...first, links: newLinks }] });
                        }}
                        placeholder="URL"
                        className="input flex-1 text-sm"
                      />
                      <button
                        onClick={() => {
                          const first = form.columns[0] ?? { title: '', links: [] };
                          const newLinks = first.links.filter((_, idx) => idx !== li);
                          setForm({ ...form, columns: [{ ...first, links: newLinks }] });
                        }}
                        className="text-danger hover:text-danger"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {renderAppearanceSettings()}

          <div className="card">
            <SectionHeader
              icon={Phone}
              title="Copyright"
              description="Wyświetlany po prawej stronie stopki"
              accent="success"
            />
            <input
              type="text"
              value={form.copyrightText}
              onChange={(e) => setForm({ ...form, copyrightText: e.target.value })}
              placeholder="© 2026 Twoja Firma. Wszelkie prawa zastrzeżone."
              className="input w-full"
            />
          </div>
        </>
      )}

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz stopkę" />

      {/* WP Media Picker Modal */}
      {showMediaPicker && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onSelect={(url) => {
            setForm((prev) => ({ ...prev, logoUrl: url }));
            setShowMediaPicker(false);
          }}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      {/* Confirm reset footer modal */}
      {confirmReset && (
        <ConfirmResetModal
          loading={resettingFooter}
          onConfirm={handleResetFooter}
          onClose={() => setConfirmReset(false)}
        />
      )}
    </div>
  );
}

// ─── Confirm reset modal ────────────────────────────────────────────────────

function ConfirmResetModal({
  loading,
  onConfirm,
  onClose,
}: {
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface-raised border border-border shadow-xl">
        <div className="flex items-start justify-between p-5 border-b border-border">
          <h3 className="text-base font-semibold text-text-primary">Przywrócić domyślny szablon EcomFlow Footer?</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-text-secondary">
            Obecna zawartość zakładki „EcomFlow Footer" zostanie zastąpiona oryginalnym szablonem. Zakładka „Własny projekt" pozostanie nietknięta. Poprzednia wersja zostanie zapisana w historii zmian buildera.
          </p>
        </div>
        <div className="flex items-center gap-3 p-4 border-t border-border">
          <button
            onClick={onConfirm}
            disabled={loading}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Przywróć domyślne
          </button>
          <button onClick={onClose} disabled={loading} className="btn-secondary text-sm">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
